// Fabrication maths for the Blueprint Creator.
export const MATERIAL_OPTIONS = [
  { value: "steel", label: "Steel (galvanised)", stock_length_mm: 6500 },
  { value: "aluminium", label: "Aluminium", stock_length_mm: 6000 },
];

export const POST_PROFILES = {
  steel: ["50x50 SHS", "65x65 SHS", "75x75 SHS", "89x89 SHS", "100x100 SHS", "40NB Pipe", "50NB Pipe", "80NB Pipe"],
  aluminium: ["50x50", "65x65", "75x75", "90x90"],
};

const NB_OD = { 40: 48, 50: 60, 80: 89 };
export function profileSize(profile) {
  const nb = String(profile || "").match(/^(\d+)NB/);
  if (nb) return NB_OD[Number(nb[1])] || Number(nb[1]);
  const m = String(profile || "").match(/^(\d+)/);
  return m ? Number(m[1]) : 65;
}

// Presets applied when the structure type changes — every field stays editable after.
export const STRUCTURE_PRESETS = {
  fence: { label: "Fence", height_mm: 1800, rail_count: 2, infill: "pickets" },
  pool_fence: { label: "Pool Fence", height_mm: 1200, rail_count: 2, infill: "pickets", picket_gap_mm: 90 },
  handrail: { label: "Handrail", height_mm: 1000, rail_count: 2, infill: "none" },
  balustrade: { label: "Balustrade", height_mm: 1000, rail_count: 2, infill: "pickets", picket_gap_mm: 99 },
  privacy_screen: { label: "Privacy Screen (slats)", height_mm: 1800, rail_count: 2, infill: "slats" },
  mesh_screen: { label: "Mesh Screen / Panel", height_mm: 1800, rail_count: 2, infill: "mesh" },
  gate_frame: { label: "Gate / Gate Frame", height_mm: 1800, rail_count: 2, infill: "pickets" },
  security_fence: { label: "Security Fence (358 / mesh)", height_mm: 2400, rail_count: 2, infill: "mesh", mesh_aperture_mm: 76, panel_width_mm: 2400 },
  roll_top: { label: "Roll Top Fence", height_mm: 1800, rail_count: 0, infill: "mesh", mesh_aperture_mm: 50, panel_width_mm: 2500 },
  safety_barrier: { label: "Safety Barrier (anti-climb / anti-ligature)", height_mm: 2400, rail_count: 2, infill: "pickets", picket_gap_mm: 80, panel_width_mm: 2400 },
  other: { label: "Other / Custom", height_mm: 1800, rail_count: 2, infill: "none" },
};

export const COLOUR_OPTIONS = [
  { name: "Monument", hex: "#323233" }, { name: "Satin Black", hex: "#202124" }, { name: "Night Sky", hex: "#101820" },
  { name: "Basalt", hex: "#6D6C6E" }, { name: "Woodland Grey", hex: "#4B4C46" }, { name: "Shale Grey", hex: "#BDBFBA" },
  { name: "Surfmist", hex: "#E4E2D5" }, { name: "Pearl White", hex: "#F3F4F0" }, { name: "Paperbark", hex: "#CABFA4" },
  { name: "Deep Ocean", hex: "#364152" }, { name: "Cottage Green", hex: "#304C3C" }, { name: "Manor Red", hex: "#5E1D24" },
  { name: "Galvanised", hex: "#9EA3A8" },
];

export const TOPPING_OPTIONS = [
  { value: "none", label: "No topping" },
  { value: "barbed", label: "Barbed wire (3 strand)" },
  { value: "razor", label: "Razor / concertina wire" },
  { value: "rotating", label: "Rotating anti-climb topper" },
  { value: "electric", label: "Electric fence topping" },
];

// Australian Standards style checks — returned as {level: error|warn|info, msg}
export function complianceChecks(sIn) {
  const s = { ...DEFAULT_SETTINGS, ...sIn };
  const out = [];
  const st = s.structure_type;
  if (st === "safety_barrier") {
    if (s.height_mm < 2400) out.push({ level: "error", msg: `Safety barrier height ${s.height_mm} mm is below the 2400 mm recommended minimum — increase height.` });
    if (s.infill !== "pickets") out.push({ level: "error", msg: "Anti-ligature barriers must use vertical-only infill (no horizontal footholds) — switch infill to pickets/balusters." });
    if (s.infill === "pickets" && s.picket_gap_mm > 100) out.push({ level: "warn", msg: `Picket gap ${s.picket_gap_mm} mm — keep ≤ 100 mm to prevent footholds/grip points.` });
    out.push({ level: "info", msg: "Structural engineering certification required for this installation — CCFI will coordinate." });
    out.push({ level: "info", msg: "Use concealed fixings; no exposed bolt heads on the accessible face." });
  }
  if (st === "security_fence") {
    if (s.height_mm < 2100) out.push({ level: "warn", msg: `Security fencing at ${s.height_mm} mm — standard commercial minimum is 2100–2400 mm.` });
    if ((s.topping || "none") === "electric") out.push({ level: "info", msg: "Electric topping — licensed electrician referral required. CCFI will coordinate energiser install and commissioning." });
    else if ((s.topping || "none") !== "none") out.push({ level: "info", msg: `Topping specified: ${s.topping} — include topping arm detail on fabrication drawing.` });
  }
  if (st === "roll_top" && s.height_mm < 900) out.push({ level: "warn", msg: `Roll top fencing below 900 mm — standard range is 900–2400 mm.` });
  if (st === "pool_fence") {
    if (s.height_mm < 1200) out.push({ level: "error", msg: `Pool barrier height ${s.height_mm} mm is below the AS 1926 minimum of 1200 mm.` });
    if (s.infill === "pickets" && s.picket_gap_mm > 100) out.push({ level: "error", msg: `Picket gap ${s.picket_gap_mm} mm exceeds the AS 1926 maximum of 100 mm.` });
  }
  if ((st === "handrail" || st === "balustrade") && s.height_mm < 865) {
    out.push({ level: "error", msg: `Rail height ${s.height_mm} mm is below the AS 1657 minimum of 865 mm.` });
  }
  return out;
}

export const INFILL_OPTIONS = [
  { value: "pickets", label: "Pickets / balusters (vertical)" },
  { value: "slats", label: "Slats (horizontal)" },
  { value: "louvre", label: "Louvre slats (angled — privacy + airflow)" },
  { value: "blade", label: "Blade fence (flat bar architectural)" },
  { value: "laser_cut", label: "Custom laser-cut sheet" },
  { value: "mesh", label: "Mesh panels" },
  { value: "panels", label: "Sheet panels" },
  { value: "none", label: "Rails only" },
];

export const BLADE_ORIENTATIONS = [
  { value: "vertical", label: "Vertical blades" },
  { value: "horizontal", label: "Horizontal blades" },
];

export const DEFAULT_SETTINGS = {
  scale_m_per_cell: 0.5,
  structure_type: "fence",
  material: "steel",
  post_profile: "65x65 SHS",
  panel_width_mm: 2400, // max post spacing — centres are evened out per run
  post_size_mm: 65,
  rail_count: 2,
  infill: "pickets", // pickets | slats | louvre | blade | laser_cut | mesh | panels | none
  picket_width_mm: 65,
  picket_gap_mm: 100,
  slat_width_mm: 65,
  slat_gap_mm: 10,
  // Louvre blades — angled horizontal blades for privacy + airflow
  louvre_width_mm: 100,
  louvre_gap_mm: 20,
  louvre_angle_deg: 45,
  // Blade fencing — thin flat-bar architectural blades
  blade_orientation: "vertical",
  blade_width_mm: 50,
  blade_thickness_mm: 6,
  blade_gap_mm: 20,
  // Custom laser-cut sheet
  laser_cut_preset: "botanical_leaf",
  laser_cut_design_name: "",
  laser_cut_upload_url: "",
  laser_cut_sheet_thickness_mm: 3,
  mesh_aperture_mm: 50,
  topping: "none",
  height_mm: 1200,
  stock_length_mm: 6500,
};

export const CELL_PX = 24;

export function segLengthM(p1, p2, scale) {
  return (Math.hypot(p2.x - p1.x, p2.y - p1.y) / CELL_PX) * scale;
}

export function calcBlueprint(points, segments, settings) {
  const s = { ...DEFAULT_SETTINGS, ...settings };
  const rows = [];
  let totalM = 0, posts = 0, centrePosts = 0, panels = 0, rails = 0, pickets = 0, slats = 0, louvres = 0, blades = 0, laserSheets = 0, meshPanels = 0, gates = 0;
  const railCuts = {}; // clear length mm -> rail qty
  const slatCuts = {}; // clear length mm -> slat qty
  const louvreCuts = {}; // clear length mm -> louvre qty
  const bladeCuts = {}; // cut key -> qty (varies by orientation)
  const laserCuts = {}; // "{w}x{h}" -> qty of sheets
  const meshCuts = {}; // clear length mm -> panel qty

  for (let i = 0; i < points.length - 1; i++) {
    const meta = segments[i] || { type: "fence" };
    const autoLen = segLengthM(points[i], points[i + 1], s.scale_m_per_cell);
    const lengthM = meta.length_m != null && meta.length_m > 0 ? meta.length_m : Math.round(autoLen * 100) / 100;
    const L = lengthM * 1000;
    totalM += lengthM;

    if (meta.type === "gate") {
      gates += 1;
      rows.push({ index: i, type: "gate", length_m: lengthM, panels: 0, bay_mm: 0, rail_cut_mm: 0, pickets: 0, gap_mm: 0, centre_posts: 0 });
      continue;
    }
    // Even centres: minimum bays so no span exceeds max post spacing, all bays equal.
    const segPanels = Math.max(1, Math.ceil(L / s.panel_width_mm));
    const bay = L / segPanels;
    const clear = Math.max(0, bay - s.post_size_mm);
    const railCut = Math.round(clear);

    // Vertical pickets: even gaps per bay, never exceeding the max gap.
    let n = 0, gap = 0;
    if (s.infill === "pickets" && clear > s.picket_width_mm) {
      n = Math.max(0, Math.ceil((clear - s.picket_gap_mm) / (s.picket_width_mm + s.picket_gap_mm)));
      gap = n >= 0 ? (clear - n * s.picket_width_mm) / (n + 1) : 0;
      while (gap > s.picket_gap_mm) {
        n += 1;
        gap = (clear - n * s.picket_width_mm) / (n + 1);
      }
      if (gap < 0) { n = Math.floor(clear / s.picket_width_mm); gap = 0; }
    }

    // Horizontal slats: count from height, gaps evened between slats.
    let nSlats = 0, slatGap = 0;
    if (s.infill === "slats" && s.height_mm > s.slat_width_mm) {
      nSlats = Math.max(1, Math.floor((s.height_mm + s.slat_gap_mm) / (s.slat_width_mm + s.slat_gap_mm)));
      slatGap = nSlats > 1 ? (s.height_mm - nSlats * s.slat_width_mm) / (nSlats - 1) : 0;
      slatCuts[railCut] = (slatCuts[railCut] || 0) + nSlats * segPanels;
      slats += nSlats * segPanels;
    }

    // Louvre blades: angled overlapping horizontals. Effective vertical spacing = width * cos(angle) + gap.
    let nLouvres = 0, louvreVertPitch = 0;
    if (s.infill === "louvre" && s.height_mm > s.louvre_width_mm) {
      const angleRad = ((s.louvre_angle_deg || 45) * Math.PI) / 180;
      const projected = s.louvre_width_mm * Math.cos(angleRad);
      const pitch = Math.max(1, projected + s.louvre_gap_mm);
      nLouvres = Math.max(1, Math.floor(s.height_mm / pitch));
      louvreVertPitch = s.height_mm / nLouvres;
      louvreCuts[railCut] = (louvreCuts[railCut] || 0) + nLouvres * segPanels;
      louvres += nLouvres * segPanels;
    }

    // Blade fence: thin flat-bar blades, orientation-dependent.
    let nBlades = 0, bladeGap = 0, bladeLen = 0;
    if (s.infill === "blade") {
      if (s.blade_orientation === "horizontal") {
        // horizontal blades stacked over height
        nBlades = Math.max(1, Math.floor((s.height_mm + s.blade_gap_mm) / (s.blade_width_mm + s.blade_gap_mm)));
        bladeGap = nBlades > 1 ? (s.height_mm - nBlades * s.blade_width_mm) / (nBlades - 1) : 0;
        bladeLen = railCut;
      } else {
        // vertical blades across bay
        nBlades = Math.max(1, Math.floor((clear + s.blade_gap_mm) / (s.blade_width_mm + s.blade_gap_mm)));
        bladeGap = nBlades > 1 ? (clear - nBlades * s.blade_width_mm) / (nBlades - 1) : 0;
        bladeLen = s.height_mm;
      }
      const key = `${bladeLen}`;
      bladeCuts[key] = (bladeCuts[key] || 0) + nBlades * segPanels;
      blades += nBlades * segPanels;
    }

    // Custom laser-cut sheet: one sheet per bay, cut to clear width × internal height.
    if (s.infill === "laser_cut") {
      const innerH = s.height_mm - 2 * (s.post_size_mm > 100 ? 40 : 30); // rough allowance for top/bottom rail
      const key = `${railCut}x${innerH}`;
      laserCuts[key] = (laserCuts[key] || 0) + segPanels;
      laserSheets += segPanels;
    }

    if (s.infill === "mesh") {
      meshCuts[railCut] = (meshCuts[railCut] || 0) + segPanels;
      meshPanels += segPanels;
    }

    panels += segPanels;
    rails += segPanels * s.rail_count;
    pickets += n * segPanels;
    if (s.rail_count > 0) railCuts[railCut] = (railCuts[railCut] || 0) + segPanels * s.rail_count;
    rows.push({
      index: i, type: "fence", length_m: lengthM, panels: segPanels,
      bay_mm: Math.round(bay), rail_cut_mm: railCut, centre_posts: segPanels - 1,
      pickets: n * segPanels, pickets_per_bay: n, gap_mm: Math.round(gap * 10) / 10,
      slats_per_bay: nSlats, slat_gap_mm: Math.round(slatGap * 10) / 10,
      louvres_per_bay: nLouvres, louvre_pitch_mm: Math.round(louvreVertPitch * 10) / 10,
      blades_per_bay: nBlades, blade_gap_mm: Math.round(bladeGap * 10) / 10, blade_orient: s.blade_orientation,
    });
    posts += segPanels;
    centrePosts += segPanels - 1;
  }
  if (rows.length) posts += 1; // closing post on the run

  const matLabel = s.material === "aluminium" ? "aluminium" : "steel";
  const cutList = Object.entries(railCuts)
    .filter(([len]) => Number(len) > 0)
    .sort((a, b) => b[0] - a[0])
    .map(([len, qty]) => ({
      item: `Rail (${matLabel}, cut to clear span)`, cut: `${len} mm`, qty,
      spacing: "-", for: `bays needing ${len} mm rails`,
      perStock: Math.max(1, Math.floor(s.stock_length_mm / Number(len))),
    }));
  let stockBars = 0;
  for (const rowc of cutList) stockBars += Math.ceil(rowc.qty / rowc.perStock);

  if (s.infill === "pickets" && pickets > 0) {
    const perBays = rows.filter((r) => r.type === "fence" && r.pickets_per_bay > 0);
    const gapsDesc = [...new Set(perBays.map((r) => r.gap_mm))].slice(0, 3).join(" / ");
    cutList.push({
      item: `Picket / baluster (${s.picket_width_mm} mm ${matLabel})`, cut: `${s.height_mm} mm`, qty: pickets,
      spacing: `${gapsDesc} mm gaps`, for: "equal spacing per bay — weld at marks",
    });
  }
  if (s.infill === "slats" && slats > 0) {
    const perBays = rows.filter((r) => r.type === "fence" && r.slats_per_bay > 0);
    const gapsDesc = [...new Set(perBays.map((r) => r.slat_gap_mm))].slice(0, 3).join(" / ");
    for (const [len, qty] of Object.entries(slatCuts).sort((a, b) => b[0] - a[0])) {
      cutList.push({
        item: `Slat (${s.slat_width_mm} mm ${matLabel}, horizontal)`, cut: `${len} mm`, qty,
        spacing: `${gapsDesc} mm gaps`, for: `${perBays[0]?.slats_per_bay || 0} slats per bay, evened over ${s.height_mm} mm`,
      });
    }
  }
  if (s.infill === "louvre" && louvres > 0) {
    const perBays = rows.filter((r) => r.type === "fence" && r.louvres_per_bay > 0);
    const pitchDesc = [...new Set(perBays.map((r) => r.louvre_pitch_mm))].slice(0, 3).join(" / ");
    for (const [len, qty] of Object.entries(louvreCuts).sort((a, b) => b[0] - a[0])) {
      cutList.push({
        item: `Louvre blade (${s.louvre_width_mm} mm ${matLabel}, angled ${s.louvre_angle_deg}°)`, cut: `${len} mm`, qty,
        spacing: `${pitchDesc} mm pitch`,
        for: `${perBays[0]?.louvres_per_bay || 0} louvres per bay @ ${s.louvre_angle_deg}° — welded to internal blade brackets`,
      });
    }
    cutList.push({
      item: `Louvre blade bracket (${matLabel})`,
      cut: `internal frame — ${louvres * 2} weld points`,
      qty: louvres * 2, spacing: "-", for: "one bracket set holds each blade at the fixed angle",
    });
  }
  if (s.infill === "blade" && blades > 0) {
    const orient = s.blade_orientation === "horizontal" ? "horizontal" : "vertical";
    const perBays = rows.filter((r) => r.type === "fence" && r.blades_per_bay > 0);
    const gapsDesc = [...new Set(perBays.map((r) => r.blade_gap_mm))].slice(0, 3).join(" / ");
    for (const [len, qty] of Object.entries(bladeCuts).sort((a, b) => b[0] - a[0])) {
      cutList.push({
        item: `Blade — ${s.blade_width_mm}×${s.blade_thickness_mm} mm flat bar (${matLabel}, ${orient})`,
        cut: `${len} mm`, qty, spacing: `${gapsDesc} mm gaps`,
        for: `${perBays[0]?.blades_per_bay || 0} blades per bay — architectural blade fence, welded ends`,
      });
    }
  }
  if (s.infill === "laser_cut" && laserSheets > 0) {
    const designName = s.laser_cut_design_name || (s.laser_cut_preset === "custom" ? "Custom uploaded design" : s.laser_cut_preset);
    for (const [dims, qty] of Object.entries(laserCuts).sort((a, b) => (b[0] || "").localeCompare(a[0] || ""))) {
      cutList.push({
        item: `Laser-cut sheet — ${designName} (${s.laser_cut_sheet_thickness_mm} mm ${matLabel})`,
        cut: `${dims.replace("x", " × ")} mm`, qty, spacing: "-",
        for: "supply DXF/DWG to laser cutter — one sheet per bay, framed on all edges",
      });
    }
    cutList.push({
      item: "Laser-cut design file (DXF / DWG)",
      cut: `${s.laser_cut_sheet_thickness_mm} mm sheet, ${matLabel}`,
      qty: 1, spacing: "-", for: `Design: ${designName}`,
    });
  }
  if (s.infill === "mesh" && meshPanels > 0) {
    for (const [len, qty] of Object.entries(meshCuts).sort((a, b) => b[0] - a[0])) {
      cutList.push({
        item: `Mesh panel (${s.mesh_aperture_mm} mm aperture, ${matLabel})`, cut: `${len} × ${s.height_mm} mm`, qty,
        spacing: "-", for: "one panel per bay, fixed to frame",
      });
    }
  }
  cutList.push({
    item: `Post ${s.post_profile || `${s.post_size_mm} mm`} (${matLabel})`,
    cut: `${s.height_mm + 600} mm (incl. 600 in-ground)`, qty: posts,
    spacing: rows.length ? `${[...new Set(rows.filter((r) => r.type === "fence").map((r) => r.bay_mm))].slice(0, 3).join(" / ")} mm centres` : "-",
    for: `${posts - centrePosts} end/corner + ${centrePosts} centre posts`,
  });

  return {
    segments: rows,
    compliance: complianceChecks(s),
    totals: {
      total_m: Math.round(totalM * 100) / 100,
      posts, centre_posts: centrePosts, panels, rails, pickets, slats, louvres, blades, laser_sheets: laserSheets, mesh_panels: meshPanels, gates,
      stock_bars: stockBars,
    },
    cut_list: cutList,
  };
}
